import { ChangeDetectionStrategy, Component, effect, inject, untracked } from '@angular/core';
import { type ActivatedRouteSnapshot, Router, RouterOutlet } from '@angular/router';

import { AuthStore } from './core/auth-store';
import { authGuard, safeReturnUrl } from './core/auth.guard';
import { useRouteFocus } from './core/route-focus';
import { SiteStore } from './core/site-store';
import { SiteUnavailable } from './pages/site-unavailable/site-unavailable';

/** Проверка домена стартует вместе с приложением, но старт не блокирует: у страниц свои скелетоны. */
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SiteUnavailable],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (site.isBlocked()) {
      <app-site-unavailable />
    } @else {
      <router-outlet />
    }
  `,
})
export class App {
  protected readonly site = inject(SiteStore);
  private readonly auth = inject(AuthStore);
  private readonly router = inject(Router);

  constructor() {
    useRouteFocus();
    // Имя и фото могли поменяться на другом устройстве; без токена метод сам ничего не делает.
    this.auth.refreshProfile();

    // Гуард срабатывает только на навигации: сессия, умершая на открытой странице, увела бы её в гостевой вид.
    let checked = false;
    effect(() => {
      if (this.auth.isAuthed()) {
        checked = false;
        return;
      }
      // Вход через Telegram ещё летит: гостя не уводим. Читаем до checked = true — конец полёта перезапустит эффект.
      if (this.auth.telegramPending()) {
        return;
      }
      // Ждём конца навигации (чтение с трекингом): до него активно старое дерево — после «Выйти» ещё /profile.
      if (checked || this.router.currentNavigation() !== null) {
        return;
      }
      // Раз за потерю сессии: упавший переход на /login (офлайн) иначе повторялся бы по кругу.
      checked = true;
      untracked(() => {
        const { root } = this.router.routerState.snapshot;
        for (let r: ActivatedRouteSnapshot | null = root; r; r = r.firstChild) {
          if (r.routeConfig?.canActivate?.includes(authGuard)) {
            void this.router.navigate(['/login'], {
              queryParams: { returnUrl: safeReturnUrl(this.router.url) },
            });
            return;
          }
        }
      });
    });
  }
}
